import { isUndefined, omitBy } from 'lodash-es'
import type {
  SearchApiSuggestion,
  SearchApiSuggestionType,
} from '~/api/search-api.types'
import type { SuggestSuggestionV22 } from '~/types/openapi/search-api'

export type AutosuggestItem = {
  type: SearchApiSuggestionType
  code: string
  label: string
  flag: string
  path: string
  selected: boolean
}

export const SUGGESTIONS_DEBOUNCE = 300

export const SUGGESTIONS_TYPES_ORDER = [
  'C', // Country
  'R', // Region
  'P', // Place
  'H', // Holiday house
  'A', // Apartment
  'B', // Boat
  'G', // Guestroom
]

export function flatSuggestions<
  T extends SearchApiSuggestion | SuggestSuggestionV22,
>(items: T[]): T[] {
  return items
    .filter(({ type }) => type && SUGGESTIONS_TYPES_ORDER.includes(type))
    .toSorted(
      (a, b) =>
        SUGGESTIONS_TYPES_ORDER.indexOf(a.type ?? '') -
        SUGGESTIONS_TYPES_ORDER.indexOf(b.type ?? ''),
    )
    .flatMap((item) => [
      item,
      ...(item.type === 'C' ? (item.topRegions ?? []) : []),
      ...(item.type === 'R' ? (item.topPlaces ?? []) : []),
    ]) as T[]
}

export function mapDestinationToSuggestion(
  destination: ConfDestination | undefined,
) {
  if (!destination) return

  const { urlPath } = destination
  const { country, region, place } = getDestinationQuery(destination)

  if (!urlPath || !country) return

  const slug = removeLanguagePrefix(urlPath)
    .split('/')
    .filter(Boolean)
    .slice(0, place ? 3 : region ? 2 : 1)
    .join('/')

  return omitBy(
    {
      type: place ? 'P' : region ? 'R' : 'C',
      code: place ?? region ?? country ?? '',
      label: getDestinationName(destination),
      country,
      region,
      slug,
    } satisfies SearchApiSuggestion,
    isUndefined,
  ) as unknown as SearchApiSuggestion
}

export function isSuggestionTypeAccom(
  type: SearchApiSuggestionType | SuggestSuggestionV22['type'],
): boolean {
  return type ? ['H', 'A', 'B', 'G'].includes(type) : false
}

export function mapRedesignDestinationToSuggestion(
  destination: ConfDestination | undefined,
) {
  if (!destination) return

  const { urlPath } = destination
  const { country, region, place } = getDestinationQuery(destination)

  if (!urlPath || !country) return

  const slug = removeLanguagePrefix(urlPath)
    .split('/')
    .filter(Boolean)
    .slice(0, place ? 3 : region ? 2 : 1)
    .join('/')
  const label = getDestinationName(destination)

  return omitBy(
    {
      type: place ? 'P' : region ? 'R' : 'C',
      code: place ?? region ?? country ?? '',
      label,
      country: country
        ? {
            code: country,
            label,
            slug,
            type: 'C',
            coordinates: { lat: 0, lon: 0 },
          }
        : undefined,
      region: region
        ? {
            code: region,
            label,
            slug,
            type: 'R',
            coordinates: { lat: 0, lon: 0 },
          }
        : undefined,
      slug,
    },
    isUndefined,
  ) as unknown as SuggestSuggestionV22
}
